import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Circle, Loader2, XCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import {
  WEB_SCRAPER_STAGES,
  fetchWebScraperJob,
  fetchWebScraperJobForProject,
  normalizeWebScraperSteps,
} from '@/lib/webScraperPipeline';

function stageIcon(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'COMPLETED') return <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />;
  if (s === 'FAILED') return <XCircle className="w-4 h-4 text-red-600 shrink-0" />;
  if (s === 'RUNNING') return <Loader2 className="w-4 h-4 text-violet-600 animate-spin shrink-0" />;
  return <Circle className="w-4 h-4 text-slate-300 shrink-0" />;
}

export function WebScraperPipelinePanel({
  projectId,
  jobId: jobIdProp,
  snapshot,
  compact = false,
}) {
  const [remote, setRemote] = useState(null);

  const jobId = jobIdProp || snapshot?.jobId || remote?.jobId;

  useEffect(() => {
    if (!projectId && !jobId) return undefined;
    let cancelled = false;

    const poll = async () => {
      try {
        const data = jobId
          ? await fetchWebScraperJob(jobId)
          : await fetchWebScraperJobForProject(projectId);
        if (!cancelled) setRemote(data);
      } catch {
        /* ignore transient poll errors */
      }
    };

    poll();
    const id = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [projectId, jobId]);

  const payload = remote || snapshot || null;
  const steps = useMemo(() => {
    const normalized = normalizeWebScraperSteps(payload);
    if (normalized.length) return normalized;
    return WEB_SCRAPER_STAGES.map((s) => ({
      ...s,
      status: 'PENDING',
      current: 0,
      total: 0,
      progressPercent: 0,
    }));
  }, [payload]);

  const overall = Number(payload?.progress) || 0;
  const errors = payload?.errors || snapshot?.errors || [];

  return (
    <div className={`rounded-lg border border-sky-200 bg-sky-50/60 ${compact ? 'p-3' : 'p-4'} space-y-3`}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-sky-800">
          Web scraper pipeline
        </p>
        {payload?.topic ? (
          <p className="text-xs text-sky-700/80 mt-0.5 truncate" title={payload.topic}>
            Topic: {payload.topic}
          </p>
        ) : null}
        {payload?.message || snapshot?.message ? (
          <p className="text-sm text-slate-700 mt-1">
            {payload?.message || snapshot?.message}
          </p>
        ) : null}
      </div>
      <Progress value={Math.min(100, Math.max(0, overall))} className="h-2" />
      <ul className="space-y-2">
        {steps.map((step) => (
          <li key={step.key} className="flex items-start gap-2 text-sm">
            {stageIcon(step.status)}
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-800">{step.label}</span>
                <span className="text-xs text-slate-500 shrink-0">
                  {step.total > 0 ? `${step.current} / ${step.total}` : step.status}
                </span>
              </div>
              {!compact && step.description ? (
                <p className="text-xs text-slate-500">{step.description}</p>
              ) : null}
              {step.total > 0 ? (
                <Progress value={step.progressPercent || 0} className="h-1 mt-1" />
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {errors.length ? (
        <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700 max-h-24 overflow-y-auto">
          {errors.map((err) => (
            <p key={err}>{err}</p>
          ))}
        </div>
      ) : null}
    </div>
  );
}
